import type { TFunction } from 'i18next';
import { formatClock, formatDuration } from '@/features/trips/format';
import type { OverspeedAlert } from './types';

/**
 * One overspeed episode row (slice 19) — the SECOND item shape of the alerts feed,
 * beside the geofence `AlertRow`. What a fleet manager scans for is here at a
 * glance: which vehicle, HOW FAST, against WHAT LIMIT, when. The location is on the
 * map (selecting the row centres it on the frozen `observedLat/observedLon`); this
 * row carries no place name because an episode has no zone, and inventing one would
 * be the fabricated-data defect this codebase refuses.
 *
 * SEVERITY IS THE FROZEN GRADING, by margin over the frozen threshold (api
 * `OverspeedAlertZ.severity`, seam S1) — the web NEVER re-derives it. It drives the
 * tag colour the way the geofence row's severity does, on the SAME existing tokens
 * (a token that does not exist renders colourless — the `text-raad-danger` bug this
 * feature already shipped once):
 *   - critical → `--raad-error`  (red)
 *   - warning  → `--raad-warn`   (amber)
 *   - info     → `--raad-slate`  (muted)
 * `info` is a real breach that is barely over; it must not shout, matching the
 * geofence "normal" row's restraint. Every episode is a genuine breach, so unlike
 * the geofence feed there is no non-alerting direction to mute further.
 *
 * NO CROSSING-INSTANT FABRICATION and none is possible here: an episode carries an
 * honest window (`startedAt`/`endedAt`, `endedAt: null` = still open). The row shows
 * when it opened and how long it ran, never a single invented moment.
 *
 * `read`/unread is a leading dot + a weight change — never colour alone.
 */

/** Tailwind token classes for the severity tag, keyed by the three tiers. Mirrors
 *  `AlertRow`'s `tagClass` deliberately — one visual language across the feed. */
function tagClass(severity: OverspeedAlert['severity']): string {
  if (severity === 'critical') return 'bg-raad-error/15 text-raad-error';
  if (severity === 'warning') return 'bg-raad-warn/18 text-raad-warn';
  return 'bg-raad-slate/12 text-raad-slate';
}

export function OverspeedRow({
  alert,
  plate,
  locale,
  timeZone,
  selected,
  onSelect,
  t,
}: {
  alert: OverspeedAlert;
  plate?: string | null;
  locale: string;
  timeZone: string;
  selected: boolean;
  onSelect: (alert: OverspeedAlert) => void;
  t: TFunction;
}) {
  const peakKmh = Math.round(alert.peakSpeedDkmh / 10);
  const overKmh = Math.max(0, peakKmh - alert.thresholdKmh);
  const observedTime = formatClock(locale, Date.parse(alert.observedAt), timeZone);
  // The episode window: a real duration when it has closed, "in progress" while
  // still open. Never a fabricated single instant.
  const durationSec =
    alert.endedAt != null
      ? Math.max(0, Math.round((Date.parse(alert.endedAt) - Date.parse(alert.startedAt)) / 1000))
      : null;
  const windowText =
    durationSec != null
      ? t('alerts.overspeed.window', { duration: formatDuration(t, durationSec) })
      : t('alerts.overspeed.ongoing');

  const severity = alert.severity;
  const isAttention = severity !== 'info';

  const titleClass =
    isAttention && !alert.read ? 'font-semibold text-raad-text' : 'font-medium text-raad-muted';
  const dotClass = alert.read ? 'bg-transparent' : isAttention ? 'bg-raad-accent' : 'bg-raad-slate';

  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(alert)}
        aria-pressed={selected}
        data-testid="overspeed-row"
        data-severity={severity}
        data-alert-id={alert.id}
        className={[
          'w-full rounded-xl border px-3.5 py-3 text-left transition-colors',
          selected
            ? 'border-raad-primary/40 bg-raad-primary/10'
            : isAttention
              ? 'border-raad-line-7 bg-raad-tile hover:border-raad-line-15'
              : 'border-raad-line-5 bg-raad-tile/60 hover:border-raad-line-12',
        ].join(' ')}
      >
        <div className="flex items-start gap-2.5">
          <span aria-hidden="true" className={['mt-1.5 size-2 flex-none rounded-full', dotClass].join(' ')} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className={['truncate text-sm', titleClass].join(' ')}>
                {t('alerts.overspeed.headline', { peak: peakKmh })}
              </span>
              <span
                data-testid="overspeed-tag"
                className={[
                  'flex-none rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                  tagClass(severity),
                ].join(' ')}
              >
                {t(`alerts.overspeed.severity.${severity}`)}
              </span>
              {!alert.read ? <span className="sr-only">{t('alerts.unread')}</span> : null}
            </div>

            {plate ? (
              <div className="mt-0.5 truncate text-[11.5px] font-semibold text-raad-dim">{plate}</div>
            ) : null}

            {/* The limit in force and how far over — the whole point of grading by
                margin. `thresholdKmh` is FROZEN on the episode: raising the tenant
                limit later never re-labels this row. */}
            <div className="mt-1 flex flex-wrap items-center gap-x-2 text-[12px] text-raad-muted">
              <span data-testid="overspeed-limit">
                {t('alerts.overspeed.limit', { limit: alert.thresholdKmh })}
              </span>
              {overKmh > 0 ? (
                <span className="font-semibold text-raad-dim">
                  {t('alerts.overspeed.over', { over: overKmh })}
                </span>
              ) : null}
            </div>

            <div className="mt-0.5 text-[12px] text-raad-dim">
              {t('alerts.observedAt', { time: observedTime })} · {windowText}
            </div>
          </div>
        </div>
      </button>
    </li>
  );
}
